import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { toast } from 'react-toastify';
import { authService } from '../services/authService';
import { User, AuthContextType, LoginCredentials, RegisterData } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const LOCK_STORAGE_KEY = 'sysup_system_locked';
const LOCKED_USER_STORAGE_KEY = 'sysup_locked_user';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [lockedUser, setLockedUser] = useState<AuthContextType['lockedUser']>(() => {
    const storedUser = localStorage.getItem(LOCKED_USER_STORAGE_KEY);
    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser);
    } catch {
      localStorage.removeItem(LOCKED_USER_STORAGE_KEY);
      return null;
    }
  });
  const [isLocked, setIsLocked] = useState(() => localStorage.getItem(LOCK_STORAGE_KEY) === 'true');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      if (localStorage.getItem(LOCK_STORAGE_KEY) === 'true') {
        if (!localStorage.getItem(LOCKED_USER_STORAGE_KEY)) {
          localStorage.removeItem(LOCK_STORAGE_KEY);
          setIsLocked(false);
          return;
        }

        localStorage.removeItem('sysup_token');
        setUser(null);
        setIsLocked(true);
        return;
      }

      const token = localStorage.getItem('sysup_token');
      if (token) {
        try {
          const userData = await authService.getMe();
          setUser(userData);
        } catch (error: any) {
          // Se for erro de conexão, apenas remove o token silenciosamente
          // Não mostra toast para evitar problemas de inicialização
          if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || error.message?.includes('Network Error')) {
            console.warn('API não disponível. Removendo token local.');
            localStorage.removeItem('sysup_token');
          } else {
            // Para outros erros, também remove o token
            console.error('Erro ao inicializar autenticação:', error);
            localStorage.removeItem('sysup_token');
          }
        }
      }
    } catch (error) {
      console.error('Erro ao inicializar autenticação:', error);
      localStorage.removeItem('sysup_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);
      localStorage.removeItem(LOCK_STORAGE_KEY);
      localStorage.removeItem(LOCKED_USER_STORAGE_KEY);
      setIsLocked(false);
      setLockedUser(null);
      
      // Salva o token no localStorage
      localStorage.setItem('sysup_token', response.token);
      
      // Define o usuário no estado
      setUser(response.user);
      
      toast.success('Login realizado com sucesso!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao fazer login';
      toast.error(message);
      throw error;
    }
  };  

  const logout = async () => {
    try {
      // 1. Limpar Caches do Service Worker
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
        } catch (err) {
          console.warn('Erro ao limpar caches:', err);
        }
      }

      // 2. Desregistrar Service Workers
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(
            registrations.map(registration => registration.unregister())
          );
        } catch (err) {
          console.warn('Erro ao desregistrar service workers:', err);
        }
      }

      // 3. Remove o token do localStorage
      localStorage.removeItem('sysup_token');
      localStorage.removeItem(LOCK_STORAGE_KEY);
      localStorage.removeItem(LOCKED_USER_STORAGE_KEY);
      
      // Remove o usuário do estado
      setUser(null);
      setLockedUser(null);
      setIsLocked(false);
      
      // Chama o serviço de logout (para invalidar no servidor se necessário)
      authService.logout().catch(() => {}); // Ignora erros de logout
      
      toast.info('Logout realizado e cache limpo!');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      // Fallback em caso de erro crítico
      localStorage.removeItem('sysup_token');
      setUser(null);
    }
  };

  const lockSystem = () => {
    if (!user) return;

    const userToLock = {
      id: user.id,
      nome: user.nome,
      usuario: user.usuario,
      perfil: user.perfil,
      avatar: user.avatar,
    };

    localStorage.setItem(LOCK_STORAGE_KEY, 'true');
    localStorage.setItem(LOCKED_USER_STORAGE_KEY, JSON.stringify(userToLock));
    localStorage.removeItem('sysup_token');
    setLockedUser(userToLock);
    setUser(null);
    setIsLocked(true);
  };

  const unlockSystem = async (password: string) => {
    if (!lockedUser?.usuario) {
      throw new Error('Usuário bloqueado não encontrado. Faça login novamente.');
    }

    const response = await authService.unlockWithPassword({
      usuario: lockedUser.usuario,
      password,
    });

    localStorage.removeItem(LOCK_STORAGE_KEY);
    localStorage.removeItem(LOCKED_USER_STORAGE_KEY);
    setUser(response.user);
    setLockedUser(null);
    setIsLocked(false);
  };

  const updateProfile = async (data: Partial<User>) => {
    try {
      const updatedUser = await authService.updateProfile(data);
      setUser(updatedUser);
      toast.success('Perfil atualizado com sucesso!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao atualizar perfil';
      toast.error(message);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success('Senha alterada com sucesso!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao alterar senha';
      toast.error(message);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    lockedUser,
    loading,
    isLocked,
    login,
    logout,
    lockSystem,
    unlockSystem,
    updateProfile,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Hook para verificar permissões
export const usePermissions = () => {
  const { user } = useAuth();

  const can = (permissionCode: string): boolean => {
    if (!user) return false;
    if (user.perfil === 'administrador') return true;

    return Array.isArray(user.permissions) && user.permissions.includes(permissionCode);
  };

  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false;
    
    const allowedRoles = Array.isArray(roles) ? roles : [roles];
    return allowedRoles.includes(user.perfil);
  };

  const isAdmin = (): boolean => hasRole('administrador');
  const isFinancial = (): boolean => hasRole(['administrador', 'financeiro']);
  const isSupport = (): boolean => hasRole(['administrador', 'suporte']);
  const isClient = (): boolean => hasRole('cliente');

  const ehAdministrador = (): boolean => hasRole('administrador');
  const ehFinanceiro = (): boolean => hasRole(['administrador', 'financeiro']);
  const ehSuporte = (): boolean => hasRole(['administrador', 'suporte']);
  const ehCliente = (): boolean => hasRole('cliente');

  return {
    can,
    hasRole,
    isAdmin,
    isFinancial,
    isSupport,
    isClient,
    ehAdministrador,
    ehFinanceiro,
    ehSuporte,
    ehCliente,
    user,
  };
}; 
